import { Formik } from 'formik';
import { ChangeEvent, FC } from 'react';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { getOriginFormConfig, originFormSchema } from '@constants';
import { FormActionType, FormType, hostType, originType } from '@enums';
import { useAppDispatch, useFormContent, useFormProcedure } from '@hooks';
import { fetchFormList, submitFormRequest } from '@store';
import type { DialogBase, OriginConfigurationForm } from '@types';
import { determineOriginType, validateValueEqual } from '@utils';

import { initializeOriginsValues, updateOriginsByType } from '../originUtils';

export const AddOriginModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnOrigin;

  const currentForm = useFormContent({ formType });
  const currentProcedure = useFormProcedure(formType);

  const isEdit = validateValueEqual(currentProcedure, FormActionType.Edit);

  const handleSubmit = (values: OriginConfigurationForm) => {
    const { origin_type, enable_proxy_host, proxy_host, origins, verify_ssl_cert, type, ...otherValues } = values;

    const formData: OriginConfigurationForm = {
      ...(origins && {
        origins: origins.filter((entry) => entry.origin.trim() !== ''),
        type,
        verify_ssl_cert: type === originType.HTTPS ? values.verify_ssl_cert : false,
      }),
      ...(enable_proxy_host && {
        proxy_host: proxy_host,
      }),
      ...otherValues,
    };

    dispatch(
      submitFormRequest({
        formType,
        formData: isEdit ? formData : { application_id, ...formData },
        ...(isEdit && {
          params: {
            content_id: currentForm?.id,
          },
        }),
        callbackFunction: {
          refetch: fetchFormList({
            formType,
            params: { application_id },
            globalOptions: {
              pagination: true,
            },
          }),
        },
      }),
    );
  };

  const initialValues: OriginConfigurationForm = {
    name: currentForm?.name ?? '',
    type: currentForm?.type ?? originType.HTTPS,
    origins: initializeOriginsValues(currentForm),
    verify_ssl_cert: currentForm?.verify_ssl_cert ?? false,
    origin_type: determineOriginType(currentForm?.origins, hostType) ?? hostType.Ip,
    enable_proxy_host: Boolean(currentForm.proxy_host),
    proxy_host: currentForm?.proxy_host ?? '',
  };

  if (!open) return null;

  return (
    <Formik<OriginConfigurationForm>
      initialValues={initialValues}
      validationSchema={originFormSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setValues, errors }) => {
        const handleTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          const updatedOrigins = updateOriginsByType(value, values.origins);
          setValues((prevValues) => ({
            ...prevValues,
            type: value,
            origins: updatedOrigins,
          }));
        };

        const { inputList, label } = getOriginFormConfig({
          handleTypeChange,
          values,
          isEdit,
          schema: originFormSchema,
        });

        return (
          <FormModal
            formType={formType}
            title={label?.title}
            description={label?.description}
            onClose={onClose}
            size="xl"
            open={open}
            confirmButton={{ children: label?.confirmButton }}
          >
            <GFormFields inputList={inputList} touched={values} errors={errors} />
          </FormModal>
        );
      }}
    </Formik>
  );
};
